import { createClient } from "@supabase/supabase-js";
import {
  isSupabaseConfigured,
  missingSupabaseConfig,
  supabaseAnonKey,
  supabaseUrl,
} from "./config";
import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";
import { isMissingAuthSessionError, MISSING_AUTH_SESSION_MESSAGE } from "./authErrors";

const AUTH_LOCK_ERROR_PATTERN = /released because another request stole it/i;
const AUTH_NETWORK_ERROR_PATTERN =
  /failed to fetch|networkerror|network request failed|load failed/i;
const AUTH_LOCK_RETRY_COUNT = 2;
const AUTH_LOCK_RETRY_DELAY_MS = 120;

let authOperationQueue = Promise.resolve();

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const isSupabaseAuthLockError = (error) =>
  AUTH_LOCK_ERROR_PATTERN.test(error?.message || "");

const isSupabaseAuthNetworkError = (error) =>
  AUTH_NETWORK_ERROR_PATTERN.test(error?.message || "") ||
  (error?.name === "TypeError" &&
    AUTH_NETWORK_ERROR_PATTERN.test(
      `${error?.message || ""} ${error?.details || ""}`.toLowerCase()
    ));

const queueAuthOperation = (operation) => {
  const scheduledOperation = authOperationQueue.then(operation, operation);

  authOperationQueue = scheduledOperation.catch(() => undefined);

  return scheduledOperation;
};

const runSupabaseAuthOperation = (operation) =>
  queueAuthOperation(async () => {
    for (let attempt = 0; attempt <= AUTH_LOCK_RETRY_COUNT; attempt += 1) {
      try {
        const result = await operation();

        if (
          !isSupabaseAuthLockError(result?.error) ||
          attempt === AUTH_LOCK_RETRY_COUNT
        ) {
          return result;
        }
      } catch (error) {
        if (
          !isSupabaseAuthLockError(error) ||
          attempt === AUTH_LOCK_RETRY_COUNT
        ) {
          throw error;
        }
      }

      await sleep(AUTH_LOCK_RETRY_DELAY_MS * (attempt + 1));
    }

    return operation();
  });

const normalizeAuthError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  if (isMissingAuthSessionError(error)) {
    return Object.assign(new Error(MISSING_AUTH_SESSION_MESSAGE), { code: "AUTH_SESSION_MISSING" });
  }

  if (isSupabaseAuthLockError(error)) {
    return new Error(
      "La sesion se estaba sincronizando. Intenta abrir el panel otra vez en unos segundos."
    );
  }

  if (isSupabaseAuthNetworkError(error)) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return new Error(
        "No hay conexion a internet en este dispositivo. Reconecta la red y vuelve a intentar."
      );
    }

    return new Error(
      "No pudimos conectarnos con Supabase para iniciar sesion. Revisa la conexion, el bloqueo del navegador o la URL/configuracion del proyecto."
    );
  }

  return new Error(error.message || fallbackMessage);
};

const createTransientAuthClient = () =>
  createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  });

export const getInitialSession = async () => {
  ensureSupabaseReady();

  const { data, error } = await runSupabaseAuthOperation(() =>
    withRequestDeadline(
      () => supabase.auth.getSession(),
      { message: "No pudimos recuperar tu sesion a tiempo. Recarga la pagina para volver a intentar." }
    )
  );

  if (error) {
    if (isMissingAuthSessionError(error)) return null;
    throw normalizeAuthError(error, "No se pudo recuperar la sesion actual.");
  }

  return data.session || null;
};

export const getCurrentAuthenticatedUser = async () => {
  ensureSupabaseReady();

  const {
    data: { user },
    error,
  } = await runSupabaseAuthOperation(() => supabase.auth.getUser());

  if (error) {
    throw normalizeAuthError(error, "No se pudo identificar el usuario actual.");
  }

  return user || null;
};

export const listenToAuthChanges = (callback) => {
  ensureSupabaseReady();

  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session || null);
  });

  return data.subscription;
};

export const signInWithEmail = async ({ email, password }) => {
  ensureSupabaseReady();

  const { data, error } = await runSupabaseAuthOperation(() =>
    supabase.auth.signInWithPassword({
      email,
      password,
    })
  );

  if (error) {
    throw normalizeAuthError(error, "No se pudo iniciar sesion.");
  }

  return data;
};

export const signUpWithEmail = async ({ email, password, redirectTo }) => {
  ensureSupabaseReady();

  const { data, error } = await runSupabaseAuthOperation(() =>
    supabase.auth.signUp({
      email,
      password,
      options: redirectTo
        ? {
            emailRedirectTo: redirectTo,
          }
        : undefined,
    })
  );

  if (error) {
    throw normalizeAuthError(error, "No se pudo crear la cuenta.");
  }

  return data;
};

export const sendInvitationSetupEmail = async ({
  email,
  redirectTo,
  userData,
}) => {
  ensureSupabaseReady();

  const transientClient = createTransientAuthClient();
  const { data, error } = await transientClient.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
      emailRedirectTo: redirectTo,
      data: userData,
    },
  });

  if (error) {
    throw normalizeAuthError(
      error,
      "No se pudo enviar el correo de acceso al usuario invitado."
    );
  }

  return data;
};

export const requestPasswordReset = async ({ email, redirectTo }) => {
  ensureSupabaseReady();

  const { data, error } = await runSupabaseAuthOperation(() =>
    supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    })
  );

  if (error) {
    throw normalizeAuthError(error, "No se pudo enviar el correo de recuperacion.");
  }

  return data;
};

export const updateCurrentUserPassword = async ({ password }) => {
  ensureSupabaseReady();

  const { data, error } = await runSupabaseAuthOperation(() =>
    supabase.auth.updateUser({
      password,
    })
  );

  if (error) {
    throw normalizeAuthError(error, "No se pudo actualizar la contrasena.");
  }

  return data;
};

export const signOutCurrentUser = async ({ scope = "global" } = {}) => {
  ensureSupabaseReady();

  const { error } = await runSupabaseAuthOperation(() =>
    supabase.auth.signOut({ scope })
  );

  if (error) {
    throw normalizeAuthError(error, "No se pudo cerrar sesion.");
  }
};
